import { Switch } from '@headlessui/react'
import { useState, useRef, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import { useFreeVideoSynthesis } from 'hooks/query/aiStudio'
import { useInfiniteMyResults } from 'hooks/query/aiStudio/useInfiniteMyResults'
import { useFreeSynthesisStatus } from 'hooks/query/aiStudio/useFreeSynthesisStatus'
import { aiStudioService } from 'service/aiStudio'
import InfiniteScroll from 'react-infinite-scroll-component'
import { VideoData, VideoItem } from './components/VideoItem'
import { usePointManager } from 'hooks/usePointManager'
import useUserStore from 'store/useUserStore'
import { useNavigate } from 'react-router-dom'
import { PointInsufficientModal } from 'pages/ai-studio/tabs/FashionEditorTab/components/PointInsufficientModal'

export const Veo3 = () => {
  const [isDarkMode, setIsDarkMode] = useState(true)
  const [activeTab, setActiveTab] = useState<'frames' | 'ingredients'>('frames')
  const [uploadedFrames, setUploadedFrames] = useState({
    startFrame: null as File | null,
    endFrame: null as File | null,
  })
  const [uploadedIngredient, setUploadedIngredient] = useState<File | null>(
    null,
  )
  const [framePreviewUrls, setFramePreviewUrls] = useState({
    startFrame: null as string | null,
    endFrame: null as string | null,
  })
  const [ingredientPreviewUrl, setIngredientPreviewUrl] = useState<
    string | null
  >(null)
  const [prompt, setPrompt] = useState('')
  // const [isEnhanced, setIsEnhanced] = useState(true) // 주석 처리 - 사용하지 않는 기능
  // const [isMultiShot, setIsMultiShot] = useState(false) // 주석 처리 - 사용하지 않는 기능
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([])
  const [resolution, setResolution] = useState<'720p' | '1080p'>('720p')
  const [duration, setDuration] = useState(4)
  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16' | '1:1'>(
    '16:9',
  )

  const { userInfo } = useUserStore()
  const navigate = useNavigate()

  // 폴링 중인 비디오 요청들 (다중 지원)
  const [pollingVideoRequests, setPollingVideoRequests] = useState<
    {
      requestId: number
      prompt: string
      resolution: string
      durationSeconds: number
      aspectRatio: string
    }[]
  >([])

  // File input refs
  const startFrameRef = useRef<HTMLInputElement>(null)
  const endFrameRef = useRef<HTMLInputElement>(null)
  const ingredientRef = useRef<HTMLInputElement>(null)

  // Hooks
  const videoGenerationMutation = useFreeVideoSynthesis()

  // 가장 최근 요청에 대해서만 폴링 (React 훅 규칙 준수)
  const currentRequest =
    pollingVideoRequests[pollingVideoRequests.length - 1] || null
  const { data: videoStatusData } = useFreeSynthesisStatus({
    id: currentRequest?.requestId || null,
    enabled: !!currentRequest,
  })

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteMyResults({
    requestType: 'video', // Add requestType=video parameter
    enabled: !!userInfo, // Only fetch when user is logged in
  })

  // 크레딧 계산 함수
  const calculateCredits = () => {
    const creditTable = {
      '720p_4': 500,
      '1080p_4': 1000,
      '720p_8': 1000,
      '1080p_8': 1500,
    } as const

    const key = `${resolution}_${duration}` as keyof typeof creditTable
    return creditTable[key] || 500
  }

  const { currentPoint, hasEnoughPoints, checkPointsRealtime } =
    usePointManager({
      requiredPoints: calculateCredits(),
    })
  const [isPointInsufficientModalOpen, setIsPointInsufficientModalOpen] =
    useState(false)

  // 모든 이미지 상태 초기화 함수
  const clearAllImages = useCallback(() => {
    // Frames 관련 초기화
    setUploadedFrames({ startFrame: null, endFrame: null })

    // Preview URL 메모리 해제 (이전 상태를 매개변수로 받아 처리)
    setFramePreviewUrls((prev) => {
      if (prev.startFrame) URL.revokeObjectURL(prev.startFrame)
      if (prev.endFrame) URL.revokeObjectURL(prev.endFrame)
      return { startFrame: null, endFrame: null }
    })

    setIngredientPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })

    setUploadedIngredient(null)
    setUploadedImageUrls([])
  }, []) // 빈 의존성 배열로 함수 재생성 방지

  // imageMode 계산 함수
  const getImageMode = ():
    | 'first_only'
    | 'first_and_reference'
    | 'first_and_last' => {
    if (activeTab === 'ingredients') {
      return 'first_and_reference'
    }

    if (activeTab === 'frames') {
      const frameCount = [
        uploadedFrames.startFrame,
        uploadedFrames.endFrame,
      ].filter(Boolean).length

      if (frameCount === 1) return 'first_only'
      if (frameCount === 2) return 'first_and_last'
    }

    return 'first_only' // 기본값
  }

  // 파일 업로드 핸들러
  const handleFileUpload = async (
    file: File,
    type: 'startFrame' | 'endFrame' | 'ingredient',
  ) => {
    try {
      setIsUploading(true)

      // 프리뷰 URL 생성
      const previewUrl = URL.createObjectURL(file)

      if (type === 'startFrame') {
        setUploadedFrames((prev) => ({ ...prev, startFrame: file }))
        setFramePreviewUrls((prev) => ({ ...prev, startFrame: previewUrl }))
      } else if (type === 'endFrame') {
        setUploadedFrames((prev) => ({ ...prev, endFrame: file }))
        setFramePreviewUrls((prev) => ({ ...prev, endFrame: previewUrl }))
      } else if (type === 'ingredient') {
        setUploadedIngredient(file)
        setIngredientPreviewUrl(previewUrl)
      }

      const uploadResult = await aiStudioService.uploadFile(file)

      // URL 저장
      setUploadedImageUrls((prev) => {
        const newUrls = [...prev, uploadResult.fileUrl]
        return newUrls
      })
    } catch (error) {
      alert('파일 업로드에 실패했습니다.')
    } finally {
      setIsUploading(false)
    }
  }

  // 드래그 앤 드롭 핸들러
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (
    e: React.DragEvent,
    type: 'startFrame' | 'endFrame' | 'ingredient',
  ) => {
    e.preventDefault()
    e.stopPropagation()

    const files = Array.from(e.dataTransfer.files)
    const imageFile = files.find((file) => file.type.startsWith('image/'))

    if (imageFile) {
      handleFileUpload(imageFile, type)
    }
  }

  // 비디오 생성 핸들러 with polling
  const handleGenerateVideo = async () => {
    if (!userInfo) {
      navigate('/sign-up')
    }
    const hasEnoughPoints = await checkPointsRealtime()
    if (!hasEnoughPoints) {
      setIsPointInsufficientModalOpen(true)
      return
    }
    if (!prompt.trim()) {
      alert('프롬프트를 입력해주세요.')
      return
    }

    // 이미지 유효성 검사 (레퍼런스는 선택사항)

    if (activeTab === 'frames') {
      const frameCount = [
        uploadedFrames.startFrame,
        uploadedFrames.endFrame,
      ].filter(Boolean).length
      if (frameCount === 0) {
        alert('시작 프레임 또는 끝 프레임을 업로드해주세요.')
        return
      }
    }

    const imageMode = getImageMode()

    try {
      const result = await videoGenerationMutation.mutateAsync({
        userPrompt: prompt,
        uploadedImageUrls: uploadedImageUrls,
        imageMode: imageMode,
        resolution: resolution,
        durationSeconds: duration,
        aspectRatio: aspectRatio,
      })

      // 새 요청을 폴링 배열에 추가
      setPollingVideoRequests((prev) => [
        ...prev,
        {
          requestId: result.id,
          prompt: prompt,
          resolution: resolution,
          durationSeconds: duration,
          aspectRatio: aspectRatio,
        },
      ])

      // Clear form after successful generation
      setPrompt('')
      clearAllImages()
    } catch (error) {
      alert('비디오 생성 요청에 실패했습니다..')
    }
  }

  // 폴링 완료 처리
  useEffect(() => {
    if (videoStatusData && currentRequest) {
      const isComplete =
        videoStatusData.status === 'completed' ||
        videoStatusData.status === 'failed'
      if (isComplete) {
        // 완료된 요청을 배열에서 제거
        setPollingVideoRequests((prev) =>
          prev.filter((req) => req.requestId !== currentRequest.requestId),
        )
        refetch() // 완료되면 전체 리스트 새로고침
      }
    }
  }, [videoStatusData, currentRequest, refetch])

  // 탭 변경 시 이미지 초기화
  useEffect(() => {
    clearAllImages()
  }, [activeTab, clearAllImages])

  // 실제 비디오 데이터
  const realVideos: VideoData[] =
    data?.pages.flatMap((page: any) => page.items) || []

  // 모든 폴링 중인 요청에 대해 스켈레톤 생성
  const pollingSkeletons: VideoData[] = pollingVideoRequests.map((request) => ({
    jobId: -request.requestId, // 음수로 임시 ID
    resultUrl: '',
    requestType: 'video' as const,
    userPrompt: request.prompt,
    aspectRatio: request.aspectRatio,
    resolution: request.resolution,
    durationSeconds: request.durationSeconds,
    status: 'pending' as const,
    createdAt: new Date().toISOString(),
    completedAt: '',
    requestId: request.requestId,
    pointCost: 0,
  }))

  // 폴링 스켈레톤 + 실제 데이터 병합
  const allVideos: VideoData[] = [...pollingSkeletons, ...realVideos]

  return (
    <div
      className={classNames(
        'flex w-screen font-sans transition-all duration-300',
        {
          'bg-black text-white': isDarkMode,
          'bg-white text-gray-900': !isDarkMode,
        },
      )}
    >
      {/* Left Sidebar */}
      <div
        className={classNames(
          'relative flex h-screen w-1/4 flex-col border-r p-4 shadow-sm transition-all duration-300',
          {
            'border-zinc-800 bg-zinc-900': isDarkMode,
            'border-gray-200 bg-gray-50': !isDarkMode,
          },
        )}
      >
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-auto pb-20">
          {/* Theme Toggle */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2
                className={classNames('mb-1 text-xl font-bold', {
                  'text-white': isDarkMode,
                  'text-gray-900': !isDarkMode,
                })}
              >
                GENERAL
              </h2>
              <p
                className={classNames('text-sm', {
                  'text-gray-400': isDarkMode,
                  'text-gray-600': !isDarkMode,
                })}
              >
                Google VEO 3.1
              </p>
            </div>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={classNames(
                'rounded-lg p-2 transition-all hover:scale-105',
                {
                  'bg-zinc-800 text-gray-300 hover:bg-zinc-700': isDarkMode,
                  'bg-gray-200 text-gray-700 hover:bg-gray-300': !isDarkMode,
                },
              )}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
          </div>

          <div className="mb-3 flex gap-2">
            <button
              onClick={() => setActiveTab('frames')}
              className={classNames(
                'w-1/2 rounded-lg px-3 py-2 text-sm shadow-sm transition-all hover:shadow-md',
                {
                  'border border-main bg-main text-white':
                    activeTab === 'frames',
                  'border border-zinc-700 bg-zinc-800 text-gray-300 hover:bg-zinc-700':
                    isDarkMode && activeTab !== 'frames',
                  'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50':
                    !isDarkMode && activeTab !== 'frames',
                },
              )}
            >
              프레임
            </button>
            <button
              onClick={() => setActiveTab('ingredients')}
              className={classNames(
                'w-1/2 rounded-lg px-3 py-2 text-sm shadow-sm transition-all hover:shadow-md',
                {
                  'border border-main bg-main text-white':
                    activeTab === 'ingredients',
                  'border border-zinc-700 bg-zinc-800 text-gray-300 hover:bg-zinc-700':
                    isDarkMode && activeTab !== 'ingredients',
                  'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50':
                    !isDarkMode && activeTab !== 'ingredients',
                },
              )}
            >
              레퍼런스
            </button>
          </div>

          {/* 탭 콘텐츠 */}
          <div className="mb-4">
            {activeTab === 'frames' && (
              <div className="space-y-2">
                <input
                  ref={startFrameRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload(file, 'startFrame')
                  }}
                  className="hidden"
                />
                <div
                  onClick={() => startFrameRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'startFrame')}
                  className={classNames(
                    'flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-3 text-center text-sm shadow-sm transition-all hover:shadow-md',
                    {
                      'border-zinc-700 bg-zinc-800 text-gray-300 hover:border-zinc-600 hover:bg-zinc-700':
                        isDarkMode && !uploadedFrames.startFrame,
                      'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50':
                        !isDarkMode && !uploadedFrames.startFrame,
                      'border-main bg-main/10': uploadedFrames.startFrame,
                    },
                  )}
                >
                  {uploadedFrames.startFrame ? (
                    <div className="relative w-full">
                      {framePreviewUrls.startFrame && (
                        <img
                          src={framePreviewUrls.startFrame}
                          alt="Start frame preview"
                          className="mb-2 h-20 w-full rounded object-cover"
                        />
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-green-600">
                            ✅ 시작 프레임 업로드됨
                          </div>
                          <div className="truncate text-xs text-gray-500">
                            {uploadedFrames.startFrame.name}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setUploadedFrames((prev) => ({
                              ...prev,
                              startFrame: null,
                            }))
                            if (framePreviewUrls.startFrame) {
                              URL.revokeObjectURL(framePreviewUrls.startFrame)
                            }
                            setFramePreviewUrls((prev) => ({
                              ...prev,
                              startFrame: null,
                            }))
                            setUploadedImageUrls((prev) => prev.slice(1))
                          }}
                          className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-red-500 shadow hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {isUploading ? (
                        <>
                          <div className="mb-2 h-6 w-6 animate-spin rounded-full border-2 border-main border-t-transparent"></div>
                          <div>업로드 중...</div>
                          <div className="text-xs text-gray-500">
                            잠시만 기다려주세요
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-2 text-2xl">📁</div>
                          <div>+ 시작 프레임</div>
                          <div className="text-xs text-gray-500">
                            (선택 사항)
                          </div>
                          <div className="mt-1 text-xs text-gray-400">
                            클릭 또는 드래그 앤 드롭
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                <input
                  ref={endFrameRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload(file, 'endFrame')
                  }}
                  className="hidden"
                />
                <div
                  onClick={() => endFrameRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'endFrame')}
                  className={classNames(
                    'flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-3 text-center text-sm shadow-sm transition-all hover:shadow-md',
                    {
                      'border-zinc-700 bg-zinc-800 text-gray-300 hover:border-zinc-600 hover:bg-zinc-700':
                        isDarkMode && !uploadedFrames.endFrame,
                      'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50':
                        !isDarkMode && !uploadedFrames.endFrame,
                      'border-main bg-main/10': uploadedFrames.endFrame,
                    },
                  )}
                >
                  {uploadedFrames.endFrame ? (
                    <div className="relative w-full">
                      {framePreviewUrls.endFrame && (
                        <img
                          src={framePreviewUrls.endFrame}
                          alt="End frame preview"
                          className="mb-2 h-20 w-full rounded object-cover"
                        />
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-green-600">
                            ✅ 종료 프레임 업로드됨
                          </div>
                          <div className="truncate text-xs text-gray-500">
                            {uploadedFrames.endFrame.name}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setUploadedFrames((prev) => ({
                              ...prev,
                              endFrame: null,
                            }))
                            if (framePreviewUrls.endFrame) {
                              URL.revokeObjectURL(framePreviewUrls.endFrame)
                            }
                            setFramePreviewUrls((prev) => ({
                              ...prev,
                              endFrame: null,
                            }))
                            setUploadedImageUrls((prev) =>
                              prev.filter((_, index) => index !== 1),
                            )
                          }}
                          className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-red-500 shadow hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-2 text-2xl">📁</div>
                      <div>+ 종료 프레임</div>
                      <div className="text-xs text-gray-500">(선택 사항)</div>
                      <div className="mt-1 text-xs text-gray-400">
                        클릭 또는 드래그 앤 드롭
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'ingredients' && (
              <div className="space-y-2">
                <input
                  ref={ingredientRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload(file, 'ingredient')
                  }}
                  className="hidden"
                />
                <div
                  onClick={() => ingredientRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'ingredient')}
                  className={classNames(
                    'flex min-h-[140px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-3 text-center text-sm shadow-sm transition-all hover:shadow-md',
                    {
                      'border-zinc-700 bg-zinc-800 text-gray-300 hover:border-zinc-600 hover:bg-zinc-700':
                        isDarkMode && !uploadedIngredient,
                      'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50':
                        !isDarkMode && !uploadedIngredient,
                      'border-main bg-main/10': uploadedIngredient,
                    },
                  )}
                >
                  {uploadedIngredient ? (
                    <div className="relative w-full">
                      {ingredientPreviewUrl && (
                        <img
                          src={ingredientPreviewUrl}
                          alt="Ingredient preview"
                          className="mb-2 h-32 w-full rounded object-cover" // Increased from h-24 to h-32 for larger display
                        />
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-green-600">
                            ✅ 이미지 업로드됨
                          </div>
                          <div className="truncate text-xs text-gray-500">
                            {uploadedIngredient.name}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setUploadedIngredient(null)
                            if (ingredientPreviewUrl) {
                              URL.revokeObjectURL(ingredientPreviewUrl)
                            }
                            setIngredientPreviewUrl(null)
                            setUploadedImageUrls((prev) => prev.slice(0, -1))
                          }}
                          className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-red-500 shadow hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-2 text-2xl">🖼️</div>
                      <div>+ 이미지 업로드</div>
                      <div className="text-xs text-gray-500">(단일 이미지)</div>
                      <div className="mt-1 text-xs text-gray-400">
                        클릭 또는 드래그 앤 드롭
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div
            className={classNames(
              'mb-3 rounded-lg p-3 shadow-sm transition-all duration-300',
              {
                'border border-zinc-700 bg-zinc-800': isDarkMode,
                'border border-gray-200 bg-white': !isDarkMode,
              },
            )}
          >
            <p
              className={classNames('mb-1 text-xs', {
                'text-gray-400': isDarkMode,
                'text-gray-600': !isDarkMode,
              })}
            >
              상상하는 장면을 자세히 설명해주세요.
            </p>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className={classNames(
                'w-full rounded-lg p-2 text-sm transition-all focus:border-main focus:outline-none focus:ring-2 focus:ring-main',
                {
                  'border border-zinc-700 bg-zinc-900 text-gray-200':
                    isDarkMode,
                  'border border-gray-300 bg-white text-gray-900': !isDarkMode,
                },
              )}
              rows={6}
              placeholder="프롬프트..."
            />
            {/* 향상 모드 - 주석 처리됨 */}
            {/*
            <div className="mt-2 flex items-center justify-between">
              <span
                className={classNames('text-xs', {
                  'text-gray-400': isDarkMode,
                  'text-gray-600': !isDarkMode,
                })}
              >
                향상 모드
              </span>
              <Switch
                checked={isEnhanced}
                onChange={setIsEnhanced}
                className="group relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-main transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-main focus:ring-offset-2 data-[checked]:bg-main data-[unchecked]:bg-gray-200"
              >
                <span className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4 group-data-[unchecked]:translate-x-0" />
              </Switch>
            </div>
            */}
          </div>

          {/* 멀티 샷 모드 - 주석 처리됨 */}
          {/*
          <div className="mb-2 flex items-center justify-between">
            <span
              className={classNames('text-sm', {
                'text-gray-400': isDarkMode,
                'text-gray-600': !isDarkMode,
              })}
            >
              멀티 샷 모드
            </span>
            <Switch
              checked={isMultiShot}
              onChange={setIsMultiShot}
              className="group relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-main focus:ring-offset-2 data-[checked]:bg-main data-[unchecked]:bg-gray-200"
            >
              <span className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4 group-data-[unchecked]:translate-x-0" />
            </Switch>
          </div>
          */}

          <div
            className={classNames(
              'mb-2 flex items-center justify-between text-sm',
              {
                'text-gray-400': isDarkMode,
                'text-gray-600': !isDarkMode,
              },
            )}
          >
            <span>모델</span>
            <span className="font-semibold text-main">Google Veo 3.1</span>
          </div>

          <div
            className={classNames(
              'mb-2 flex items-center justify-between text-sm',
              {
                'text-gray-400': isDarkMode,
                'text-gray-600': !isDarkMode,
              },
            )}
          >
            <span>화질</span>
            <select
              value={resolution}
              onChange={(e) =>
                setResolution(e.target.value as '720p' | '1080p')
              }
              className={classNames(
                'rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-main',
                {
                  'border border-zinc-700 bg-zinc-800 text-white': isDarkMode,
                  'border border-gray-300 bg-white text-gray-900': !isDarkMode,
                },
              )}
            >
              <option value="720p">720p</option>
              <option value="1080p">1080p</option>
            </select>
          </div>

          <div
            className={classNames(
              'mb-2 flex items-center justify-between text-sm',
              {
                'text-gray-400': isDarkMode,
                'text-gray-600': !isDarkMode,
              },
            )}
          >
            <span>길이</span>
            <select
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className={classNames(
                'rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-main',
                {
                  'border border-zinc-700 bg-zinc-800 text-white': isDarkMode,
                  'border border-gray-300 bg-white text-gray-900': !isDarkMode,
                },
              )}
            >
              <option value="4">4초</option>
              <option value="8">8초</option>
            </select>
          </div>

          <div
            className={classNames(
              'mb-2 flex items-center justify-between text-sm',
              {
                'text-gray-400': isDarkMode,
                'text-gray-600': !isDarkMode,
              },
            )}
          >
            <span>비율</span>
            <select
              value={aspectRatio}
              onChange={(e) =>
                setAspectRatio(e.target.value as '16:9' | '9:16' | '1:1')
              }
              className={classNames(
                'rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-main',
                {
                  'border border-zinc-700 bg-zinc-800 text-white': isDarkMode,
                  'border border-gray-300 bg-white text-gray-900': !isDarkMode,
                },
              )}
            >
              <option value="16:9">16:9</option>
              <option value="9:16">9:16</option>
              <option value="1:1">1:1</option>
            </select>
          </div>
        </div>

        {/* Sticky Generate Button Area */}
        <div
          className={classNames(
            'sticky bottom-0 z-10 border-t pt-4 backdrop-blur-sm',
            {
              'border-zinc-700 bg-gradient-to-t from-zinc-900 to-zinc-900/80':
                isDarkMode,
              'border-gray-200 bg-gradient-to-t from-gray-50 to-gray-50/80':
                !isDarkMode,
            },
          )}
        >
          <button
            onClick={handleGenerateVideo}
            disabled={isUploading || videoGenerationMutation.isPending}
            className={classNames(
              'flex w-full transform justify-center gap-2 rounded-xl py-3 font-bold text-white transition-all hover:scale-105 hover:shadow-lg',
              {
                'bg-main hover:bg-hover':
                  !isUploading && !videoGenerationMutation.isPending,
                'cursor-not-allowed bg-gray-400':
                  isUploading || videoGenerationMutation.isPending,
              },
            )}
          >
            {isUploading ? (
              '업로드 중...'
            ) : videoGenerationMutation.isPending ? (
              '생성 중...'
            ) : (
              <>
                <span>생성하기</span>
                <span className="flex items-center gap-[2px]">
                  <img
                    src="/assets/img/cash.png"
                    alt="check_circle"
                    className="h-[13px] w-[11px]"
                  />
                  {calculateCredits()}
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Right Video List Section with Infinite Scroll */}
      <div
        className={classNames('flex-1 p-6 transition-all duration-300', {
          'bg-zinc-950': isDarkMode,
          'bg-white': !isDarkMode,
        })}
      >
        <div className="mb-6 flex items-center justify-between">
          <h1
            className={classNames('text-2xl font-bold', {
              'font-[ubuntu] text-main': isDarkMode,
              'text-gray-900': !isDarkMode,
            })}
          >
            생성된 비디오
          </h1>
          <span
            className={classNames('rounded-full px-3 py-1 text-sm', {
              'bg-zinc-800 text-gray-400': isDarkMode,
              'bg-gray-100 text-gray-600': !isDarkMode,
            })}
          >
            {allVideos.length}개 비디오
          </span>
        </div>

        {/* 초기 로딩 상태 */}
        {isLoading && !data && (
          <div className="flex items-center justify-center py-12">
            <div
              className={classNames('text-center', {
                'text-gray-400': isDarkMode,
                'text-gray-600': !isDarkMode,
              })}
            >
              <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-main border-t-transparent"></div>
              <p>비디오 목록을 불러오는 중...</p>
            </div>
          </div>
        )}

        {/* 에러 상태 */}
        {isError && (
          <div className="flex items-center justify-center py-12">
            <div
              className={classNames('text-center', {
                'text-red-400': isDarkMode,
                'text-red-600': !isDarkMode,
              })}
            >
              <p>비디오 목록을 불러오는데 실패했습니다.</p>
            </div>
          </div>
        )}

        {/* 비디오 리스트 with Infinite Scroll */}
        {!isLoading && !isError && data && (
          <div>
            {allVideos.length === 0 ? (
              <div
                className={classNames('py-16 text-center', {
                  'text-gray-400': isDarkMode,
                  'text-gray-600': !isDarkMode,
                })}
              >
                <p className="mb-2 text-lg">아직 생성된 비디오가 없습니다</p>
                <p className="text-sm">
                  왼쪽 패널에서 새로운 비디오를 생성해보세요!
                </p>
              </div>
            ) : (
              <div
                id="video-scrollable-div"
                className="h-screen min-h-[600px] overflow-auto"
              >
                <InfiniteScroll
                  dataLength={allVideos.length}
                  next={fetchNextPage}
                  hasMore={!!hasNextPage}
                  loader={
                    <div className="py-4 text-center">
                      <div
                        className={classNames('text-sm', {
                          'text-gray-400': isDarkMode,
                          'text-gray-600': !isDarkMode,
                        })}
                      >
                        {isFetchingNextPage
                          ? '더 많은 비디오를 불러오는 중...'
                          : '로딩 중...'}
                      </div>
                    </div>
                  }
                  scrollableTarget="video-scrollable-div"
                  endMessage={
                    <div className="py-4 text-center">
                      <div
                        className={classNames('text-sm', {
                          'text-gray-500': isDarkMode,
                          'text-gray-400': !isDarkMode,
                        })}
                      >
                        더 이상 불러올 비디오가 없습니다
                      </div>
                    </div>
                  }
                >
                  <div className="space-y-4">
                    {allVideos.map((video) => (
                      <VideoItem
                        key={video.jobId}
                        video={video}
                        isDarkMode={isDarkMode}
                        onDelete={() => {
                          // TODO: 삭제 API 호출
                        }}
                        onSettingsClick={() => {
                          // TODO: 설정 클릭 처리
                        }}
                      />
                    ))}
                  </div>
                </InfiniteScroll>
              </div>
            )}
          </div>
        )}
      </div>
      {/* 포인트 부족 모달 */}
      <PointInsufficientModal
        isOpen={isPointInsufficientModalOpen}
        onClose={() => setIsPointInsufficientModalOpen(false)}
        currentPoint={currentPoint}
        requiredPoint={calculateCredits()}
      />
    </div>
  )
}
